import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import * as XLSX from "xlsx";

import {
  Search,
  Download,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

import { getSmsLogs } from "../../services/api";

import "./smsLogs.css";

export default function SMSLogs() {
  const [logs, setLogs] = useState([]);

  const [loading, setLoading] =
    useState(false);

  const [search, setSearch] =
    useState("");

  const [statusFilter, setStatusFilter] =
    useState("");

  const [fromDate, setFromDate] =
  useState("");

  const [toDate, setToDate] =
    useState("");

  const [currentPage, setCurrentPage] =
    useState(1);

  const rowsPerPage = 10;

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    try {
      setLoading(true);

      const res = await getSmsLogs();

      setLogs(res.data || []);
    } catch (err) {
      console.error(
        "Failed to load SMS logs",
        err
      );
    } finally {
      setLoading(false);
    }
  };

  // =========================================
  // FILTERED LOGS
  // =========================================

  const filteredLogs = useMemo(() => {
  return logs.filter((l) => {
    const q =
      search.toLowerCase();

    const matchesSearch =
      !q ||
      l.phone
        ?.toLowerCase()
        .includes(q) ||
      l.message
        ?.toLowerCase()
        .includes(q) ||
      l.status
        ?.toLowerCase()
        .includes(q) ||
      l.event_type
        ?.toLowerCase()
        .includes(q);

    const matchesStatus =
      !statusFilter ||
      l.status === statusFilter;

    // =========================================
    // DATE FILTER
    // =========================================

    const sentDate = new Date(
      l.sent_at
    );

    let matchesDate = true;

    if (fromDate) {
      matchesDate =
        matchesDate &&
        sentDate >=
          new Date(fromDate);
    }

    if (toDate) {
      const endDate =
        new Date(toDate);

      endDate.setHours(
        23,
        59,
        59,
        999
      );

      matchesDate =
        matchesDate &&
        sentDate <= endDate;
    }

    return (
      matchesSearch &&
      matchesStatus &&
      matchesDate
    );
  });
}, [
  logs,
  search,
  statusFilter,
  fromDate,
  toDate,
]);

  // =========================================
  // PAGINATION
  // =========================================

  const totalPages = Math.ceil(
    filteredLogs.length /
      rowsPerPage
  );

  const paginatedLogs =
    filteredLogs.slice(
      (currentPage - 1) *
        rowsPerPage,
      currentPage * rowsPerPage
    );

  // =========================================
  // EXPORT EXCEL
  // =========================================

  const exportExcel = () => {
    const exportData =
      filteredLogs.map((l) => ({
        Phone: l.phone,
        Message: l.message,
        Status: l.status,
        EventType:
          l.event_type,
        SMSLength:
          l.sms_length,
        SMSUnits:
          l.sms_units,
        Encoding:
          l.sms_encoding,
        SentAt: new Date(
          l.sent_at
        ).toLocaleString(),
      }));

    const ws =
      XLSX.utils.json_to_sheet(
        exportData
      );

    const wb =
      XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(
      wb,
      ws,
      "SMS Logs"
    );

    XLSX.writeFile(
      wb,
      `sms_logs_${Date.now()}.xlsx`
    );
  };

  return (
    <div className="card sms-logs-page">
      {/* ========================================= */}
      {/* HEADER */}
      {/* ========================================= */}

      <div className="sms-header">
        <div>
          <h3>SMS Logs</h3>

          <p>
            Total Logs:{" "}
            <strong>
              {filteredLogs.length}
            </strong>
          </p>
        </div>

        <button
          className="export-btn"
          onClick={exportExcel}
        >
          <Download size={18} />
          Export
        </button>
      </div>

      {/* ========================================= */}
      {/* FILTERS */}
      {/* ========================================= */}

      <div className="sms-filters">
        <div className="search-box">
          <Search size={16} />

          <input
            type="text"
            placeholder="Search phone, message, status..."
            value={search}
            onChange={(e) => {
              setSearch(
                e.target.value
              );

              setCurrentPage(1);
            }}
          />
        </div>

        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(
              e.target.value
            );

            setCurrentPage(1);
          }}
        >
          <option value="">
            All Status
          </option>

          <option value="SENT">
            SENT
          </option>

          <option value="FAILED">
            FAILED
          </option>

          <option value="PENDING">
            PENDING
          </option>
        </select>
        <input
          type="date"
          value={fromDate}
          onChange={(e) => {
            setFromDate(
              e.target.value
            );

            setCurrentPage(1);
          }}
        />

        <input
          type="date"
          value={toDate}
          onChange={(e) => {
            setToDate(
              e.target.value
            );

            setCurrentPage(1);
          }}
        />

        <button
  className="clear-btn"
  onClick={() => {
    setSearch("");
    setStatusFilter("");
    setFromDate("");
    setToDate("");
    setCurrentPage(1);
  }}
>
  Clear Filters
</button>
      </div>

      {/* ========================================= */}
      {/* TABLE */}
      {/* ========================================= */}

      <div className="table-wrapper">
        <table className="table">
          <thead>
            <tr>
              <th>Phone</th>
              <th>Message</th>
              <th>Status</th>
              {/* <th>Type</th> */}
              <th>SMS Length</th>
              <th>Units</th>
              {/* <th>Encoding</th> */}
              <th>Sent At</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td
                  colSpan="8"
                  style={{
                    textAlign:
                      "center",
                  }}
                >
                  Loading...
                </td>
              </tr>
            ) : paginatedLogs.length ===
              0 ? (
              <tr>
                <td
                  colSpan="8"
                  style={{
                    textAlign:
                      "center",
                  }}
                >
                  No SMS logs found
                </td>
              </tr>
            ) : (
              paginatedLogs.map(
                (l) => (
                  <tr
                    key={l.sms_id}
                  >
                    <td>
                      {l.phone}
                    </td>

                    <td
                      className="message-cell"
                    >
                      {l.message}
                    </td>

                    <td>
                      <span
                        className={`status-badge ${String(
                          l.status
                        ).toLowerCase()}`}
                      >
                        {l.status}
                      </span>
                    </td>

                    {/* <td>
                      {
                        l.event_type
                      }
                    </td> */}

                    <td>
                      {l.sms_length ||
                        "-"}
                    </td>

                    <td>
                      {l.sms_units ||
                        "-"}
                    </td>

                    {/* <td>
                      {l.sms_encoding ||
                        "-"}
                    </td> */}

                    <td>
                      {new Date(
                        l.sent_at
                      ).toLocaleString()}
                    </td>
                  </tr>
                )
              )
            )}
          </tbody>
        </table>
      </div>

      {/* ========================================= */}
      {/* PAGINATION */}
      {/* ========================================= */}

      {totalPages > 1 && (
        <div className="pagination">
          <button
            disabled={
              currentPage === 1
            }
            onClick={() =>
              setCurrentPage(
                (p) => p - 1
              )
            }
          >
            <ChevronLeft
              size={16}
            />
          </button>

          <span>
            Page{" "}
            <strong>
              {currentPage}
            </strong>{" "}
            of{" "}
            <strong>
              {totalPages}
            </strong>
          </span>

          <button
            disabled={
              currentPage ===
              totalPages
            }
            onClick={() =>
              setCurrentPage(
                (p) => p + 1
              )
            }
          >
            <ChevronRight
              size={16}
            />
          </button>
        </div>
      )}
    </div>
  );
}